import { useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Product } from './types';
export const productSetup = {
    [Product.CollaLite]: {
        name: 'CollaLite',
        color: '#6842d2',
    },
    [Product.ElastiLite]: {
        name: 'ElastiLite',
        color: '#f69933',
    },
    [Product.KeraLite]: {
        name: 'CollaLite',
        color: '#d9484e',
    },
    [Product.LamiLite]: {
        name: 'CollaLite',
        color: '#4282d3',
    },
    [Product.ResiLite]: {
        name: 'CollaLite',
        color: '#b1d342',
    },
    [Product.SeaLite]: {
        name: 'CollaLite',
        color: '#42d3c2',
    },
};
export const loadShader = (path) => {
    return fetch(path).then((response) => {
        if (!response.ok) {
            throw new Error(`Failed to load shader: ${path}`);
        }
        return response.text();
    });
};
/**
 * Converts a hex color value to an RGB object.
 * @param hex - The hex color value (e.g., "#000000" or "000000").
 * @returns An object with properties {r, g, b}.
 */
export const hexToRgb = (hex) => {
    const sanitizedHex = hex.startsWith('#') ? hex.slice(1) : hex;
    if (!/^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(sanitizedHex)) {
        throw new Error('Invalid hex color value');
    }
    // Expand shorthand hex (e.g., "03F") to full form (e.g., "0033FF")
    const fullHex = sanitizedHex.length === 3
        ? sanitizedHex
            .split('')
            .map((char) => char + char)
            .join('')
        : sanitizedHex;
    // Convert to RGB values
    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);
    return { r, g, b };
};
/**
 * Normalizes an RGB object to values between 0 and 1.
 * @param rgb - An object with properties {r, g, b} where each is a number between 0 and 255.
 * @returns An object with normalized {r, g, b} values between 0 and 1.
 */
export const normalizeRgb = (rgb) => {
    return {
        r: rgb.r / 255,
        g: rgb.g / 255,
        b: rgb.b / 255,
    };
};
export const getRandomStringItem = (arr) => {
    if (arr.length === 0) {
        console.warn('Array cannot be empty');
    }
    const randomIndex = Math.floor(Math.random() * arr.length);
    const item = arr[randomIndex];
    const newArr = arr.filter((_, index) => index !== randomIndex);
    return { item, arr: newArr };
};
const isDesktopSafari = () => {
    const ua = navigator.userAgent;
    return (ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Mobile'));
};
export const useSafariPageReload = () => {
    useEffect(() => {
        const handlePopState = () => {
            if (isDesktopSafari()) {
                window.location.reload();
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);
};
const distanceEase = 0.24;
export const getAnimDistance = (ref, addition = 1) => {
    if (!ref)
        return { x: 0, y: 0 };
    const bounds = ref.getBoundingClientRect();
    const distance = {
        x: bounds.x + bounds.width * 0.5 - window.innerWidth / 2,
        y: bounds.y + bounds.height * 0.5 - window.innerHeight / 2,
    };
    return {
        x: distance.x * distanceEase * addition,
        y: distance.y * distanceEase * addition * 1.3,
    };
};
export const convertDateFormat = (dateStr) => {
    if (dateStr.length !== 8) {
        return '';
    }
    // Extract year, month, and day parts from the string.
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    // Create a Date object. Note: JavaScript months are 0-indexed, so subtract 1.
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    // Get the full month name using toLocaleString.
    const monthName = date.toLocaleString('default', { month: 'short' });
    // Get the day of the month (this will automatically remove any leading zero).
    const dayNumber = date.getDate();
    // Format the date as "Month Day Year".
    return `${monthName} ${dayNumber} ${year}`;
};
export const sanitize = (text) => DOMPurify.sanitize(text, {
    ADD_ATTR: ['target'],
});
export const validateEmail = (email) => {
    if (!email)
        return false;
    // Regular expression for basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};
export const getOriginalBoundingBox = (element) => {
    const boundingBox = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    const transform = style.transform;
    if (transform === 'none') {
        return boundingBox; // No transform applied, return as is
    }
    // Parse the transform matrix
    const matrix = new DOMMatrix(transform);
    const offsetX = matrix.m41; // Translation X
    const offsetY = matrix.m42; // Translation Y
    const scaleX = Math.hypot(matrix.a, matrix.b); // Scale X
    const scaleY = Math.hypot(matrix.c, matrix.d); // Scale Y
    // Compute the original bounding box before translation and scaling
    return {
        x: (boundingBox.x - offsetX) / scaleX,
        y: (boundingBox.y - offsetY) / scaleY,
        width: boundingBox.width / scaleX,
        height: boundingBox.height / scaleY,
        top: (boundingBox.top - offsetY) / scaleY,
        left: (boundingBox.left - offsetX) / scaleX,
        right: (boundingBox.right - offsetX) / scaleX,
        bottom: (boundingBox.bottom - offsetY) / scaleY,
    };
};
